import { useMemo, useState } from 'react';
import { useOperators, useSkills } from '../../DataProvider';
import { useProfiles } from '@eldritchtools/shared-components';
import TriggerSelector from './TriggerSelector';
import { projectGraph, useInteractionsGraph } from './graph';
import { GraphView } from './GraphView';
import SkillTooltip from './SkillTooltip';
import { ReactFlowProvider } from '@xyflow/react';
import GraphDetails from './GraphDetails';
import { OperatorImage } from '../../components/ImageHandler';
import { selectStyle } from '../../styles';
import Select from 'react-select';

function OperatorSelector({ value, setValue, options }) {
    const [operators, operatorsLoading] = useOperators();

    const optionsMapped = useMemo(() =>
        operatorsLoading ?
            {} :
            options.reduce((acc, opt) => {
                const opId = opt.opId ?? opt.id;
                const opName = opt.name ?? operators[opId].name;

                acc[opt.id] = {
                    label: <div style={{ display: "flex", alignItems: "center", gap: "0.2rem" }}>
                        <OperatorImage id={opId} width={20} height={24} />
                        {opName}
                    </div>,
                    value: opt.id,
                    name: opName
                };
                return acc;
            }, {}),
        [options, operators, operatorsLoading]
    );

    const optionsFinal = useMemo(() =>
        Object.values(optionsMapped).sort((a, b) => a.value.localeCompare(b.value)), [optionsMapped]
    );

    const filterFunc = (candidate, input) => {
        if (!input || input.length === 0) return true;
        return candidate.data.name.toLowerCase().includes(input.toLowerCase());
    }

    return <Select
        isClearable={true}
        options={optionsFinal}
        value={value ? optionsMapped[value] : value}
        onChange={opt => setValue(opt ? opt.value : opt)}
        filterOption={filterFunc}
        styles={selectStyle}
    />
}


function GraphControls({ viewSpec, onChange }) {
    const [skills, skillsLoading] = useSkills();
    const { profileData } = useProfiles();

    const availableOperators = useMemo(() =>
        skillsLoading ? [] :
            Object.entries(skills).filter(([id, data]) => {
                const opId = data.opId ?? id;
                return opId in profileData.operators ? !profileData.operators[opId].disabled : true
            }).map(([id, data]) => {
                const opt = { id: id };
                if (data.opId) opt.opId = data.opId;
                if (data.name) opt.name = data.name;
                return opt;
            }),
        [skills, skillsLoading, profileData.operators]
    )

    if (skillsLoading) return null;

    const selector = () => {
        switch (viewSpec.mode) {
            case 'operator':
                return <div style={{ display: "flex", gap: "0.25rem", alignItems: "center", flexWrap: "wrap" }}>
                    Select an operator to view their interactions:
                    <OperatorSelector
                        value={viewSpec.ids[0]}
                        setValue={v => onChange({ ...viewSpec, ids: [v] })}
                        options={availableOperators} />
                </div>
            case 'trigger':
                return <div style={{ display: "flex", flexDirection: "column", alignItems: "start", gap: "0.2rem", textAlign: "start" }}>
                    <div style={{ display: "flex", gap: "0.25rem", alignItems: "center", flexWrap: "wrap" }}>
                        Select a trigger to view the operators that interact with it:
                        <TriggerSelector value={viewSpec.ids[0]} setValue={v => onChange({ ...viewSpec, ids: [v] })} />
                    </div>
                    Some of the options are not implemented since they aren't used as triggers or are universal to all operators.
                </div>
            case 'team':
                return <div style={{ display: "flex", flexDirection: "column", alignItems: "start", gap: "0.5rem", textAlign: "start" }}>
                    <div>
                        Select up to 4 operators to view their interactions between each other:
                        <button onClick={() => onChange({ ...viewSpec, ids: [null, null, null, null] })}>Clear</button>
                    </div>
                    <div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap" }}>
                        {
                            viewSpec.ids.map((id, ind) =>
                                <OperatorSelector
                                    key={ind}
                                    value={id}
                                    setValue={v => onChange({ ...viewSpec, ids: viewSpec.ids.map((x, i) => ind === i ? v : x) })}
                                    options={availableOperators}
                                />
                            )
                        }
                    </div>
                </div>
            case 'path':
                return <div style={{ display: "flex", flexDirection: "column", alignItems: "start", gap: "0.5rem", textAlign: "start" }}>
                    <div>
                        Select two operators to see how one can interact with another:
                        <button onClick={() => onChange({ ...viewSpec, ids: [null, null] })}>Clear</button>
                    </div>
                    <div style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}>
                        <OperatorSelector
                            value={viewSpec.ids[0]}
                            setValue={v => onChange({ ...viewSpec, ids: [v, viewSpec.ids[1]] })}
                            options={availableOperators}
                        />
                        ➔
                        <OperatorSelector
                            value={viewSpec.ids[1]}
                            setValue={v => onChange({ ...viewSpec, ids: [viewSpec.ids[0], v] })}
                            options={availableOperators}
                        />
                    </div>
                </div>
            default:
                return null;
        }

    }
    return <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            Select type of interactions to view:
            <select
                value={viewSpec.mode.type}
                onChange={e => {
                    switch (e.target.value) {
                        case "operator":
                            onChange({ mode: "operator", ids: [null] })
                            return;
                        case "trigger":
                            onChange({ mode: "trigger", ids: [null] })
                            return;
                        case "team":
                            onChange({ mode: "team", ids: [null, null, null, null] })
                            return;
                        case "path":
                            onChange({ mode: "path", ids: [null, null] })
                            return;
                        default:
                            return;
                    }
                }}
            >
                <option value="operator">Operator</option>
                <option value="trigger">Trigger</option>
                <option value="team">Team</option>
                {/* <option value="path">Path</option> */}
            </select>
        </div>
        {selector()}
    </div>;
}

export default function OperatorInteractionsMapTab() {
    const [viewSpec, setViewSpec] = useState({
        mode: "operator",
        ids: [null]
    });

    const [graph, graphLoading] = useInteractionsGraph();
    const [selectedNode, setSelectedNode] = useState(null);
    const subgraph = useMemo(() => projectGraph(graph, viewSpec), [graph, viewSpec]);

    return <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <h2 style={{ marginBottom: 0 }}>Operator Interactions Map</h2>
        <p style={{ textAlign: "start", maxWidth: "1200px", lineHeight: "1.3" }}>
            Find interactions between operators.
            <br />
            The positions of the nodes on the graph are automatically and somewhat randomly computed which is still pretty rough, especially when there's a lot being displayed.
            <br />
            Clicking on a skill or status will highlight all the other nodes connected to it. This should make viewing things easier. You can also move nodes around if you want.
            <br />
            Operators can be disabled in the profiles page if you don't want them to show up. This setting is shared with the Dijiang Planner.
        </p>
        <GraphControls viewSpec={viewSpec} onChange={setViewSpec} />
        {graphLoading ?
            null :
            <ReactFlowProvider>
                <GraphView graph={subgraph} selectedNode={selectedNode} setSelectedNode={setSelectedNode} />
            </ReactFlowProvider>
        }
        <GraphDetails graph={subgraph} viewSpec={viewSpec} selectedNode={selectedNode} />
        <SkillTooltip />
    </div>;
}
